import {
  WarningAmberRounded,
  SyncProblemOutlined as ResetIcon,
  AutoModeOutlined as RestartIcon,
} from "@mui/icons-material";
import { Box, Button, Typography, useTheme } from "@mui/material";
import { useCallback, useMemo, useState } from "react";
import {
  DialogPrompt,
  DialogPromptModalType,
} from "../../components/DialogPrompt/DialogPrompt";
import {
  sendAnonymousResetRequest,
  sendAnonymousRestartRequest,
} from "../../utils/systemUtils";
import { useTranslation } from "react-i18next";
import { LOGIN_PAGE_NS } from "./i18n";
import { useNotifications } from "../../hooks/useNotifications";

export const SyncError = () => {
  // We don't know the origin of the web front-end but we can retrieve it from
  // the redirect_uri query param in the encoded ReturnUrl of the current address
  // This is needed to interact with the electron app and restart/reset the system
  const returnUrl = new URL(window.location.href).searchParams.get(
    "ReturnUrl"
  )!;
  const redirectUriSearchParam = returnUrl.split("&")[1];
  const encodedRedirectUri = redirectUriSearchParam.split("=")[1];
  const redirectUri = decodeURIComponent(encodedRedirectUri);
  const redirectOrigin = new URL(redirectUri).origin;

  const notifications = useNotifications();
  const { t } = useTranslation(LOGIN_PAGE_NS);
  const {
    palette: { warning, text },
  } = useTheme();

  const [modalType, setModalType] = useState(DialogPromptModalType.None);
  const [isOpenModal, setIsOpenModal] = useState(false);

  const closeModal = () => setIsOpenModal(false);
  const openModal = (modalType: DialogPromptModalType) => {
    setModalType(modalType);
    setIsOpenModal(true);
  };

  const tryResetSystem = useCallback(
    () =>
      (async () => {
        const willReset = await sendAnonymousResetRequest(redirectOrigin);
        if (!willReset) {
          notifications.error({
            message: t("syncing.notification.message"),
            description: t("syncing.notification.reset"),
          });
        }
      })(),
    [notifications, redirectOrigin, t]
  );

  const tryRestartSystem = useCallback(
    () =>
      (async () => {
        const willRestart = await sendAnonymousRestartRequest(redirectOrigin);
        if (!willRestart) {
          notifications.error({
            message: t("syncing.notification.message"),
            description: t("syncing.notification.restart"),
          });
        }
      })(),
    [notifications, redirectOrigin, t]
  );

  const modalActions = useMemo(
    () => ({
      [DialogPromptModalType.Reset]: tryResetSystem,
      [DialogPromptModalType.Restart]: tryRestartSystem,
      [DialogPromptModalType.None]: () => {},
    }),
    [tryResetSystem, tryRestartSystem]
  );

  const handleModalAction = useCallback(() => {
    modalActions[modalType]();
    closeModal();
  }, [modalActions, modalType]);

  const dialogPrompt = useMemo(
    () => (
      <DialogPrompt
        isOpenModal={isOpenModal}
        handleCloseModal={closeModal}
        modalType={modalType}
        handleActionModal={handleModalAction}
      />
    ),
    [handleModalAction, isOpenModal, modalType]
  );

  return (
    <Box>
      <Box
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <WarningAmberRounded sx={{ color: warning.main, fontSize: "6em" }} />
        <Typography mt={2} align={"center"}>
          {t("syncing.message")}
        </Typography>
      </Box>
      <Box mt={2}>
        <Button
          onClick={() => openModal(DialogPromptModalType.Restart)}
          startIcon={<RestartIcon />}
          size={"large"}
          sx={{ color: text.primary }}
        >
          {t("syncing.button.restart")}
        </Button>
        <Button
          onClick={() => openModal(DialogPromptModalType.Reset)}
          startIcon={<ResetIcon />}
          size={"large"}
          sx={{ color: text.primary }}
        >
          {t("syncing.button.reset")}
        </Button>
        {dialogPrompt}
      </Box>
    </Box>
  );
};
